import React, { useMemo, createContext, PropsWithChildren, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import userAnalytics from '@godaddy/user-analytics-js';
import { getAnalyticsConfig } from '../redux/selectors/getAnalyticsConfig';
import { EVENT_MAP, EventType } from '../constants/analyticsEvents';
import { setThunkExtras } from '../redux/thunks/setThunkExtras';
import { useDispatch } from '../hooks/useDispatch';

export type AnalyticsTrackerContextType = {
  track: (eventKey: EventType, payload?: Record<string, any>) => void;
  trackOnce: (eventKey: EventType, payload?: Record<string, any>) => void;
};

export const AnalyticsTrackerContext = createContext<AnalyticsTrackerContextType>({
  track: () => {},
  trackOnce: () => {}
});

export const AnalyticsTrackerProvider = ({ children }: PropsWithChildren<any>) => {
  const analyticsConfig = useSelector(getAnalyticsConfig);
  const dispatch = useDispatch();
  const trackRef = useRef<Array<string>>([]);

  const tracker = useMemo(() => {
    const traffic2Tracker = userAnalytics(analyticsConfig);
    traffic2Tracker.updateEventMap(EVENT_MAP);

    const trackOnce = (eventKey: EventType, payload?: Record<string, any>) => {
      const hashValue = JSON.stringify({ eventKey, payload });
      if (!trackRef.current.includes(hashValue)) {
        trackRef.current.push(hashValue);
        if (payload) {
          traffic2Tracker.track(eventKey, payload);
        } else {
          traffic2Tracker.track(eventKey);
        }
      }
    };

    const track = (eventKey: EventType, payload?: Record<string, any>) => {
      if (payload) {
        traffic2Tracker.track(eventKey, payload);
      } else {
        traffic2Tracker.track(eventKey);
      }
    };

    return {
      track,
      trackOnce
    };
  }, [analyticsConfig]);

  useEffect(() => {
    if (tracker) {
      dispatch(setThunkExtras({ clientTracker: tracker }));
    }
  }, [dispatch, tracker]);

  return (
    <AnalyticsTrackerContext.Provider value={tracker}>{children}</AnalyticsTrackerContext.Provider>
  );
};
